/**
 * Shapes returned by `GET .../odata/MerchantOData` — **PascalCase** property names
 * (typical for ASP.NET Core OData JSON). Grid `field` values must match these names.
 */
export interface MerchantCompanyInfo {
  MerchantId: string;
  CompanyId: string;
  Name: string;
  CreatedAt?: string;
  NextSalesOrderNumber?: number;
  NextPurchaseOrderNumber?: number;
  Email?: string;
  Phone?: string;
  AddressLine1?: string;
  AddressLine2?: string;
  City?: string;
  PostalCode?: string;
  LogoUrl?: string;
}

/** One row from the MerchantOData feed (user/account + nested merchant). */
export interface MerchantODataRow {
  Id: string;
  Email?: string;
  FirstName?: string;
  LastName?: string;
  Banned?: boolean;
  CreateDate?: string;
  LastLogin?: string | null;
  ProfilePicture?: string;
  CompanyId?: string;
  MerchantId?: string;
  Merchant?: MerchantCompanyInfo;
}

/** Alias for routes and pages that refer to a loaded record. */
export type Merchant = MerchantODataRow;

export interface MerchantGridColumn {
  field: keyof MerchantODataRow | string;
  headerText: string;
  width: string;
  isPrimaryKey?: boolean;
  visible?: boolean;
  /** Syncfusion column type, e.g. `boolean` for checkboxes. */
  type?: 'string' | 'number' | 'boolean' | 'date' | 'datetime';
}

function pickProp<T>(
  r: Record<string, unknown>,
  pascal: string,
  camel: string
): T | undefined {
  if (Object.prototype.hasOwnProperty.call(r, pascal) && r[pascal] !== undefined) {
    return r[pascal] as T;
  }
  if (Object.prototype.hasOwnProperty.call(r, camel) && r[camel] !== undefined) {
    return r[camel] as T;
  }
  return undefined;
}

/** Maps nested `Merchant` / `merchant` objects to PascalCase. */
export function normalizeMerchantCompany(
  raw: unknown
): MerchantCompanyInfo | undefined {
  if (!raw || typeof raw !== 'object') {
    return undefined;
  }
  const r = raw as Record<string, unknown>;
  const MerchantId = pickProp<string>(r, 'MerchantId', 'merchantId');
  const CompanyId = pickProp<string>(r, 'CompanyId', 'companyId');
  const Name = pickProp<string>(r, 'Name', 'name');
  if (!MerchantId || !CompanyId || !Name) {
    return undefined;
  }
  return {
    MerchantId,
    CompanyId,
    Name,
    CreatedAt: pickProp(r, 'CreatedAt', 'createdAt'),
    NextSalesOrderNumber: pickProp(r, 'NextSalesOrderNumber', 'nextSalesOrderNumber'),
    NextPurchaseOrderNumber: pickProp(
      r,
      'NextPurchaseOrderNumber',
      'nextPurchaseOrderNumber'
    ),
    Email: pickProp(r, 'Email', 'email'),
    Phone: pickProp(r, 'Phone', 'phone'),
    AddressLine1: pickProp(r, 'AddressLine1', 'addressLine1'),
    AddressLine2: pickProp(r, 'AddressLine2', 'addressLine2'),
    City: pickProp(r, 'City', 'city'),
    PostalCode: pickProp(r, 'PostalCode', 'postalCode'),
    LogoUrl: pickProp(r, 'LogoUrl', 'logoUrl'),
  };
}

/**
 * Normalizes a single-entity JSON payload to PascalCase `MerchantODataRow`.
 * Prefer **PascalCase** from the API; camelCase is accepted as fallback.
 */
export function coerceMerchantODataRow(input: unknown): MerchantODataRow {
  if (!input || typeof input !== 'object') {
    return { Id: '' };
  }
  const r = input as Record<string, unknown>;
  const idRaw = pickProp<string | number>(r, 'Id', 'id');
  const Id = idRaw !== undefined && idRaw !== null ? String(idRaw) : '';
  const nested = r['Merchant'] ?? r['merchant'];
  return {
    Id,
    Email: pickProp(r, 'Email', 'email'),
    FirstName: pickProp(r, 'FirstName', 'firstName'),
    LastName: pickProp(r, 'LastName', 'lastName'),
    Banned: pickProp(r, 'Banned', 'banned'),
    CreateDate: pickProp(r, 'CreateDate', 'createDate'),
    LastLogin: pickProp<string | null>(r, 'LastLogin', 'lastLogin'),
    ProfilePicture: pickProp(r, 'ProfilePicture', 'profilePicture'),
    CompanyId: pickProp(r, 'CompanyId', 'companyId'),
    MerchantId: pickProp(r, 'MerchantId', 'merchantId'),
    Merchant: normalizeMerchantCompany(nested),
  };
}
